import {
    NotificationTone,
    NOTIFICATION_TONE_MAX_LENGTH,
    NOTIFICATION_TONE_PATTERN,
} from '@pushly/models/lib/enums/pushly-ai/notification-tone'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { IPageAnalysisError } from '../../../../interfaces/notification-field-suggestions'

/** Most variants one generation can return, and the count the quick-fill path always asks for. */
export const PUSHLY_AI_NUM_VARIATIONS = 4

/**
 * One variant to generate. `brandVoice` writes it with the domain's Brand Voice; `tone` is a preset,
 * the user's own words, or `null` for Auto.
 */
export type PushlyAiVariantSlot = {
    brandVoice: boolean
    tone: string | null
}

export type PushlyAiGenerationOptions = {
    /** The variants, in the order they show — and the order their results show in. */
    slots: PushlyAiVariantSlot[]
    /**
     * Whether the domain had a Brand Voice when these were chosen. Without one no variant uses it,
     * but each variant's switch is kept, so it comes back once a Brand Voice does.
     */
    hasBrandVoice: boolean
}

export const DEFAULT_PUSHLY_AI_GENERATION_OPTIONS: PushlyAiGenerationOptions = {
    slots: [
        { brandVoice: true, tone: null },
        { brandVoice: true, tone: null },
        { brandVoice: false, tone: null },
        { brandVoice: false, tone: null },
    ],
    hasBrandVoice: true,
}

/** Whether a variant will actually be written with the Brand Voice: never when the domain has none. */
export const usesBrandVoice = (slot: PushlyAiVariantSlot, hasBrandVoice: boolean): boolean =>
    hasBrandVoice && slot.brandVoice

/** A tone as it is sent: trimmed, inner runs of spaces collapsed, and empty meaning Auto. */
export const normalizeTone = (tone: string | null | undefined): string | null =>
    tone?.trim().replace(/\s+/g, ' ') || null

/**
 * Why a variant's tone can't be sent, or `null` when it can. The same limits the request is
 * validated against, so a tone typed here is never rejected after Generate.
 */
export function toneError(tone: string | null | undefined): string | null {
    const value = normalizeTone(tone)
    if (!value) return null
    if (value.length > NOTIFICATION_TONE_MAX_LENGTH)
        return `Keep the tone to ${NOTIFICATION_TONE_MAX_LENGTH} characters.`
    if (!NOTIFICATION_TONE_PATTERN.test(value)) return 'Use letters, spaces, hyphens and apostrophes only.'
    return null
}

/** Whether every variant can be generated as set up. */
export const generationOptionsValid = (options: PushlyAiGenerationOptions): boolean =>
    options.slots.length > 0 && options.slots.every((slot) => !toneError(slot.tone))

/**
 * The generation request fields these options stand for — one place, so the form and the request
 * can never disagree about what was asked for. One variant spec per variant, in order; each result
 * reports its variant as `request_index`.
 */
export function generationRequestFields(options: PushlyAiGenerationOptions) {
    return {
        num_variations: options.slots.length,
        variants: options.slots.map((slot) => {
            const tone = normalizeTone(slot.tone)
            return { brand_voice: usesBrandVoice(slot, options.hasBrandVoice), ...(tone ? { tone } : {}) }
        }),
    }
}

/**
 * Where each card's result sits in the response: an index into it, or `null` for a card whose
 * variant didn't come back. Each result names its card as `requestIndex`; a response without
 * them (none expected) fills the cards in order. Results no card claims are kept at the end
 * rather than dropped.
 */
export function orderByCards(
    results: { requestIndex?: number }[],
    requested?: PushlyAiGenerationOptions,
): (number | null)[] {
    const indexes = results.map((_, i) => i)
    if (!requested) return indexes

    const cards = requested.slots.length
    if (!results.some((r) => r.requestIndex !== undefined)) {
        return [...requested.slots.map((_, card) => (card < results.length ? card : null)), ...indexes.slice(cards)]
    }

    const byCard = new Map<number, number>()
    const unclaimed: number[] = []
    for (const i of indexes) {
        const card = results[i].requestIndex
        if (card !== undefined && card < cards && !byCard.has(card)) byCard.set(card, i)
        else unclaimed.push(i)
    }
    return [...requested.slots.map((_, card) => byCard.get(card) ?? null), ...unclaimed]
}

/** The options for a domain that does or doesn't have a Brand Voice. */
export const withBrandVoiceAvailability = (
    options: PushlyAiGenerationOptions,
    hasBrandVoice: boolean,
): PushlyAiGenerationOptions => ({ ...options, hasBrandVoice })

/** Calmest to punchiest — the same scale sealab deals Auto tones along (`TONE_INTENSITY`). */
const TONES_BY_INTENSITY: NotificationTone[] = ['informative', 'curious', 'surprising', 'playful', 'urgent', 'dramatic']

/** The preset tones offered for a variant; any other words the user types are sent as they are. */
export const TONE_PRESETS = TONES_BY_INTENSITY.map((tone) => ({ value: tone, label: titleCase(tone) }))

/**
 * Where a Pushly AI generation is, from choosing the variants to reviewing them. One drawer shows
 * every stage, so the variants set up here are the ones the results fill in, in the same order.
 */
export type PushlyAiGenerationFlow = {
    stage: 'setup' | 'generating' | 'review'
    options: PushlyAiGenerationOptions
    brandVoiceLoading: boolean
    /** Back in setup after a regenerate: the next request skips every cache so it returns new copy. */
    regenerating: boolean
    /** The last attempt failed, or came back empty; shown in setup so the choices can be retried as-is. */
    error: IPageAnalysisError | null
    empty: boolean
}
